import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Card,
  DatePicker,
  Select,
  Upload,
  Tooltip,
  Row,
  Col,
  message,
} from "antd";
import {
  UploadOutlined,
  CloseOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import SuccessModal from "../Events/SuccessModal";
import api from "../../../api";

const { TextArea } = Input;
const { Option } = Select;

const CreateCharity = ({ visible, onCancel, editdata, onSuccess }) => {
  const [form] = Form.useForm();
  const [imagePreview, setImagePreview] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);


  // 📌 Convert image
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  // 📌 Validate image
  const validateImage = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      messageApi.error(
        "Only JPG, JPEG and PNG formats are allowed."
      );
      return Upload.LIST_IGNORE;
    }

    const isValidSize = file.size / 1024 / 1024 <= 5;

    if (!isValidSize) {
      messageApi.error(
        "Image size should not exceed 5 MB."
      );
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  // 📌 Upload change
  const handleUploadChange = ({ fileList }) => {
    const file = fileList[0];
    if (file && validateImage(file.originFileObj)) {
      getBase64(file.originFileObj).then(setImagePreview);
      setFileList([file]);
    } else {
      setFileList([]);
      setImagePreview(null);
    }
  };

  // 📌 Date validations
  const disabledStartDate = (current) =>
    current && current < dayjs().startOf("day");

  const disabledEndDate = (current) => {
    const startDate = form.getFieldValue("startDate");
    return (
      current &&
      (current < dayjs().startOf("day") ||
        (startDate && current < startDate))
    );
  };



  useEffect(() => {
    if (editdata) {
      form.setFieldsValue({
        title: editdata.fld_charity_title,
        description: editdata.fld_description,
        itemName: editdata.fld_item_name,
        location: editdata.fld_location,
        charityPrice: editdata.fld_charity_price_in_inr,
        marketPrice: editdata.fld_market_price_inr,
        totalQty: editdata.fld_total_quantity_offered,
        userLimit: editdata.fld_user_quantity_limit,
        beneficiaryName: editdata.fld_beneficiary_name,
        email: editdata.fld_contact_email,
        mobile: editdata.fld_contact_mobile,
        status: editdata.fld_is_active === "1" ? "Active" : "Closed",

        startDate: editdata.fld_start_date
          ? dayjs(editdata.fld_start_date, "MM/DD/YYYY")
          : null,

        endDate: editdata.fld_end_date
          ? dayjs(editdata.fld_end_date, "MM/DD/YYYY")
          : null,
      });

      // ✅ Image preview
      if (editdata.fld_charity_image_path) {
        setImagePreview(editdata.fld_charity_image_path);
      }
    } else {
      form.resetFields();
      setImagePreview(null);
      setFileList([]);
    }
  }, [editdata]);

  // 📌 Submit
  const handleFinish = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();

      // ✅ Required IDs (you can make dynamic later)
      formData.append(
        "fld_rf_id",
        editdata ? editdata.fld_rf_id : ""
      );
      formData.append("fld_charity_sw_id", editdata ? editdata.fld_charity_sw_id : "");
      formData.append("fld_charity_id", editdata ? editdata.fld_charity_id : "");

      // ✅ Main fields (mapped correctly)
      formData.append("fld_charity_title", values.title);
      formData.append("fld_description", values.description);
      formData.append("fld_item_name", values.itemName);
      formData.append("fld_location", values.location);
      formData.append("fld_beneficiary_name", values.beneficiaryName)

      formData.append("fld_charity_price_in_inr", values.charityPrice);
      formData.append("fld_market_price_inr", values.marketPrice);
      formData.append("fld_total_quantity_offered", values.totalQty);
      formData.append("fld_user_quantity_limit", values.userLimit);

      formData.append("fld_contact_email", values.email);
      formData.append("fld_contact_mobile", values.mobile);

      // ✅ Date format (IMPORTANT → MM/DD/YYYY)
      formData.append(
        "fld_start_date",
        values.startDate?.format("MM/DD/YYYY")
      );

      formData.append(
        "fld_end_date",
        values.endDate?.format("MM/DD/YYYY")
      );

      formData.append(
        "fld_charity_creation_date",
        dayjs().format("MM/DD/YYYY")
      );

      if (fileList[0]?.originFileObj) {
        // ✅ New image uploaded
        formData.append("fld_charity_image_path", fileList[0].originFileObj);
      } else if (editdata?.fld_charity_image_path) {
        // ✅ Keep existing image (VERY IMPORTANT)
        formData.append(
          "fld_charity_image_path",
          editdata.fld_charity_image_path
        );
      } else {
        // ❌ Only fail in create mode
        messageApi.error("Please upload charity image");
        return;
      }

      // ✅ Meta fields
      formData.append("fld_loggedin_user_id", "admin");
      formData.append("synceddatetime", "2025-05-29 10:08:00");
      formData.append("FormCode", "102");
      formData.append("ApiKey", "kavin");
      formData.append("AppTypeNo", "3");
      formData.append("AppVersion", "1.0.0.0");


      // ✅ API call
      const res = await api.charityCreation(formData);

      if (res?.data?.status === "1") {
        setShowSuccessModal(true);
        form.resetFields();
        setFileList([]);
        setImagePreview(null);
      } else {
        messageApi.error(res?.data?.responsemessage || "Creation failed");
      }
    } catch (err) {
      console.error(err);
      messageApi.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    onSuccess && onSuccess();
  };

  return (
    <>
      {contextHolder}

      <Modal
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={1100}
        centered
        style={{ top: 20, paddingBottom: 20 }}
        styles={{
          mask: {
            backdropFilter: "blur(8px)",
            background: "rgba(0,0,0,0.35)", // lighter blur
          },
        }}
        closeIcon={<CloseOutlined />}
      >
        <Card>
          <h2>{editdata ? "Edit Charity" : "Create Charity"}</h2>

          <Form layout="vertical" form={form} onFinish={handleFinish}>
            {/* Image */}
            <Form.Item label="Charity Image" required>
              <div
                style={{
                  height: 200,
                  border: "1px dashed #d9d9d9",
                  borderRadius: 6,
                  position: "relative",
                }}
              >
                {imagePreview ? (
                  <>
                    <img
                      src={imagePreview}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 8 }}>
                      <Tooltip title="Preview">
                        <Button
                          shape="circle"
                          icon={<EyeOutlined />}
                          onClick={() => setPreviewVisible(true)}
                        />
                      </Tooltip>
                      <Tooltip title="Remove">
                        <Button
                          danger
                          shape="circle"
                          icon={<CloseOutlined />}
                          onClick={() => {
                            setImagePreview(null);
                            setFileList([]);
                          }}
                        />
                      </Tooltip>
                    </div>
                  </>
                ) : (
                  <Upload.Dragger
                    accept=".jpg,.jpeg,.png"
                    beforeUpload={(file) => {
                      const isValid = validateImage(file);

                      if (isValid !== true) {
                        return Upload.LIST_IGNORE;
                      }

                      getBase64(file).then(setImagePreview);

                      setFileList([
                        {
                          uid: file.uid,
                          name: file.name,
                          originFileObj: file,
                        },
                      ]);

                      return false;
                    }}
                    maxCount={1}
                    showUploadList={false}
                  >
                    <UploadOutlined style={{ fontSize: 24 }} />

                    <p>Upload Image</p>

                    <p style={{ color: "#888", fontSize: 12 }}>
                      Supported formats: JPG, JPEG, PNG
                    </p>

                    <p style={{ color: "#888", fontSize: 12 }}>
                      Maximum file size: 5 MB
                    </p>
                  </Upload.Dragger>
                )}
              </div>
            </Form.Item>

            {/* Title */}
            <Form.Item
              name="title"
              label="Charity Title"
              rules={[
                { required: true, message: "Enter title" },
                { min: 3, max: 100, message: "3-100 characters only" },
              ]}
            >
              <Input />
            </Form.Item>

            {/* Description */}
            <Form.Item
              name="description"
              label="Description"
              rules={[
                { required: true },
                { min: 5, max: 2000, message: "5-2000 characters only" },
              ]}
            >
              <TextArea rows={4} maxLength={2000} showCount />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="itemName"
                  label="Item Name"
                  rules={[
                    { required: true },
                    { pattern: /^[A-Za-z ]+$/, message: "Only alphabets allowed" },
                    { min: 3, max: 40 },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="location"
                  label="Location"
                  rules={[
                    { required: true },
                    { min: 3, max: 250 },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="charityPrice"
                  label="Charity Price (₹)"
                  rules={[{ required: true, message: "Enter charity price" }]}
                >
                  <Input type="number" min={1} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="marketPrice"
                  label="Market Price (₹)"
                  rules={[{ required: true, message: "Enter market price" }]}
                >
                  <Input type="number" min={1} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="totalQty"
                  label="Total Quantity Offered"
                  rules={[
                    { required: true },
                    { min: 1, message: "Enter valid quantity" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="userLimit"
                  label="User Quantity Limit"
                  rules={[{ required: true }]}
                >
                  <Input type="number" min={1} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="beneficiaryName"
                  label="Beneficiary Name"
                  rules={[
                    { required: true, message: "Enter beneficiary name" }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Created Date" required>
                  <Input value={dayjs().format("DD MMM YYYY")} disabled />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true },
                    { type: "email", message: "Enter valid email" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="mobile"
                  label="Mobile"
                  rules={[
                    { required: true },
                    { pattern: /^[0-9]{10}$/, message: "Enter valid 10 digit number" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} disabledDate={disabledStartDate} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="endDate" label="End Date">
                  <DatePicker
                    style={{ width: "100%" }}
                    disabledDate={(current) => {
                      const start = form.getFieldValue("startDate");
                      return (
                        current &&
                        (current < dayjs().startOf("day") ||
                          (start && current < start))
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* <Form.Item name="status" label="Status" rules={[{ required: true }]}>
              <Select>
                <Option value="Active">Active</Option>
                <Option value="Closed">Closed</Option>
              </Select>
            </Form.Item> */}

            <Form.Item>
              <Row justify="end">
                <div style={{ display: "flex", gap: "30px" }}>
                  <Button
                    onClick={onCancel}
                    style={{
                      width: 150,
                      height: 40,
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{
                      backgroundColor: "#1980B9",
                      borderColor: "#1980B9",
                      width: 150,
                      height: 40,
                    }}
                  >

                    {editdata ? "Update Charity" : "Create Charity"}

                  </Button>
                </div>
              </Row>
            </Form.Item>
          </Form>
        </Card>
      </Modal>

      {/* Preview */}
      <Modal open={previewVisible} footer={null} onCancel={() => setPreviewVisible(false)}>
        <img src={imagePreview} style={{ width: "100%" }} />
      </Modal>

      {/* Success */}
      {showSuccessModal && (
        <SuccessModal
          visible={showSuccessModal}
          onClose={handleCloseSuccess}
          title={
            editdata
              ? "Charity Updated Successfully"
              : "Charity Created Successfully"
          }
          description={
            editdata
              ? "Your charity has been updated successfully."
              : "Your charity has been created and published."
          }
        />
      )}
    </>
  );
};

export default CreateCharity;